// NotificationStatus.jsx
// Place in src/components/NotificationStatus.jsx
//
// Detects the browser's notification permission state and gives the
// user the right action for that state:
//   "default" (never asked)  -> button to request permission directly
//   "denied"  (blocked)      -> instructions modal, since JS cannot
//                                re-request once blocked
//   "granted" (enabled)      -> small confirmation, no action needed
//
// Usage — drop anywhere inside the logged-in app, e.g. inside
// AgentDashboard.jsx near the top, or in a Settings section:
//
//   import NotificationStatus from '../components/NotificationStatus';
//   <NotificationStatus uid={user?.uid || user?.userID} />

import React, { useState, useEffect } from 'react';
import { Box, Typography, Button, Dialog, DialogContent, DialogActions, IconButton } from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import { initFCM } from '../utils/useFCMNotifications';
//import { initFCM } from '../../utils/useFCMNotifications'
import { isMobileDevice, isIOSDevice, isStandalonePWA } from '../utils/deviceDetect';

const ACCENT = '#1a6fba';
const BG_SOFT = '#eef4fb';

function getPermissionState() {
  if (!('Notification' in window)) return 'unsupported';
  return Notification.permission; // 'granted' | 'denied' | 'default'
}

export default function NotificationStatus({ uid }) {
  const [permission, setPermission] = useState(getPermissionState());
  const [showInstructions, setShowInstructions] = useState(false);
  const [showIOSInstall, setShowIOSInstall] = useState(false);
  const [requesting, setRequesting] = useState(false);

  useEffect(() => {
    const handleFocus = () => setPermission(getPermissionState());
    window.addEventListener('focus', handleFocus);
    return () => window.removeEventListener('focus', handleFocus);
  }, []);

  // useEffect(() => {
  //   alert(
  //     'standalone: ' + window.navigator.standalone +
  //     '\nmatchMedia: ' + window.matchMedia('(display-mode: standalone)').matches +
  //     '\nUA: ' + navigator.userAgent
  //   );
  // }, []);

  if (!isMobileDevice()) {
    return null;
  }

  // iOS Safari, not installed to Home Screen — Notification API isn't
  // usable at all until the user adds the site as a PWA. Show install
  // instructions instead of a broken/no-op Enable button.
  const needsIOSInstall = isIOSDevice() && !isStandalonePWA();

  if (needsIOSInstall) {
    return (
      <>
        <Box style={S.row}>
          <span style={S.dotDefault} />
          <Typography style={S.statusText}>
            Add ThinkLead to your Home Screen to enable notifications
          </Typography>
          <Button
            variant="contained"
            size="small"
            onClick={() => setShowIOSInstall(true)}
            style={S.enableButton}
          >
            How
          </Button>
        </Box>

        <Dialog open={showIOSInstall} onClose={() => setShowIOSInstall(false)} maxWidth="xs" fullWidth>
          <IconButton
            onClick={() => setShowIOSInstall(false)}
            style={S.closeIcon}
            size="small"
          >
            <CloseIcon fontSize="small" />
          </IconButton>
          <DialogContent style={S.dialogContent}>
            <Typography variant="h6" style={S.dialogTitle}>
              Add ThinkLead to your Home Screen
            </Typography>
            <Typography style={S.dialogBody}>
              iPhone notifications only work once ThinkLead is added to your Home Screen. Here is how:
            </Typography>

            <Box style={S.stepBlock}>
              <Typography style={S.stepLabel}>Step 1</Typography>
              <Typography style={S.stepText}>
                Tap the Share icon at the bottom of Safari (the square with an arrow pointing up).
              </Typography>
            </Box>

            <Box style={S.stepBlock}>
              <Typography style={S.stepLabel}>Step 2</Typography>
              <Typography style={S.stepText}>
                Scroll down and tap "Add to Home Screen", then tap "Add" in the top corner.
              </Typography>
            </Box>

            <Box style={S.stepBlock}>
              <Typography style={S.stepLabel}>Step 3</Typography>
              <Typography style={S.stepText}>
                Open ThinkLead from the new icon on your Home Screen (not from Safari), then turn on notifications from there.
              </Typography>
            </Box>
          </DialogContent>
          <DialogActions style={S.dialogActions}>
            <Button onClick={() => setShowIOSInstall(false)} style={S.gotItButton}>
              Got it
            </Button>
          </DialogActions>
        </Dialog>
      </>
    );
  }

  const handleEnableClick = async () => {
    if (permission === 'denied') {
      setShowInstructions(true);
      return;
    }
    setRequesting(true);
    await initFCM(uid);
    setPermission(getPermissionState());
    setRequesting(false);
  };

  return (
    <>
      {/* {permission === 'granted' && (
        <Box style={S.compactRow}>
          <span style={S.dotGranted} />
          <Typography style={S.compactText}>Notifications on</Typography>
        </Box>
      )} */}
      {permission === 'default' && (
        <Box style={S.row}>
          <span style={S.dotDefault} />
          <Typography style={S.statusText}>
            Turn on notifications to hear from your agents when you are away
          </Typography>
          <Button
            variant="contained"
            size="small"
            onClick={handleEnableClick}
            disabled={requesting}
            style={S.enableButton}
          >
            {requesting ? 'Requesting…' : 'Enable'}
          </Button>
        </Box>
      )}

      {permission === 'denied' && (
        <Box style={S.row}>
          <span style={S.dotDenied} />
          <Typography style={S.statusText}>Notifications are blocked in your browser</Typography>
          <Button
            variant="outlined"
            size="small"
            onClick={() => setShowInstructions(true)}
            style={S.fixButton}
          >
            How to fix
          </Button>
        </Box>
      )}

      <Dialog open={showInstructions} onClose={() => setShowInstructions(false)} maxWidth="xs" fullWidth>
        <IconButton
          onClick={() => setShowInstructions(false)}
          style={S.closeIcon}
          size="small"
        >
          <CloseIcon fontSize="small" />
        </IconButton>
        <DialogContent style={S.dialogContent}>
          <Typography variant="h6" style={S.dialogTitle}>
            Turn notifications back on
          </Typography>
          <Typography style={S.dialogBody}>
            Your browser blocked notifications for ThinkLead, so we cannot ask again automatically. Here is how to allow them:
          </Typography>

          <Box style={S.stepBlock}>
            <Typography style={S.stepLabel}>On your phone (Chrome)</Typography>
            <Typography style={S.stepText}>
              Tap the lock or info icon next to the address bar, then Permissions, then turn on Notifications. Or go to Chrome Settings → Site settings → Notifications → find thinklead.app → Allowed.
            </Typography>
          </Box>

          <Box style={S.stepBlock}>
            <Typography style={S.stepLabel}>On desktop (Chrome)</Typography>
            <Typography style={S.stepText}>
              Click the lock icon next to the address bar, then Site settings, then change Notifications to Allow. Refresh the page afterwards.
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions style={S.dialogActions}>
          <Button onClick={() => setShowInstructions(false)} style={S.gotItButton}>
            Got it
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

const S = {
  row: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '4px 10px',
    borderRadius: '8px',
    backgroundColor: BG_SOFT,
    flexWrap: 'wrap',
  },
  dotGranted: {
    width: '8px', height: '8px', borderRadius: '50%',
    backgroundColor: '#2e7d32', flexShrink: 0,
  },
  dotDefault: {
    width: '8px', height: '8px', borderRadius: '50%',
    backgroundColor: ACCENT, flexShrink: 0,
  },
  dotDenied: {
    width: '8px', height: '8px', borderRadius: '50%',
    backgroundColor: '#c62828', flexShrink: 0,
  },
statusText: {
  fontSize: '12px',
  color: '#0d47a1',
  flex: 1,
  margin: 0,        // new
},
  enableButton: {
    backgroundColor: ACCENT,
    color: '#fff',
    fontSize: '11px',
    textTransform: 'none',
    boxShadow: 'none',
    padding: '2px 10px',
    minHeight: '28px',
  },
  fixButton: {
    borderColor: ACCENT,
    color: ACCENT,
    fontSize: '12px',
    textTransform: 'none',
  },
  closeIcon: {
    position: 'absolute',
    top: '8px',
    right: '8px',
    zIndex: 1,
  },
  dialogContent: {
    padding: '28px 24px 8px',
  },
  dialogTitle: {
    fontWeight: 700,
    color: '#0d47a1',
    marginBottom: '8px',
  },
  dialogBody: {
    fontSize: '14px',
    color: '#333',
    marginBottom: '20px',
  },
  stepBlock: {
    marginBottom: '16px',
  },
  stepLabel: {
    fontSize: '13px',
    fontWeight: 700,
    color: ACCENT,
    marginBottom: '4px',
  },
  stepText: {
    fontSize: '13px',
    color: '#444',
    lineHeight: 1.5,
  },
  dialogActions: {
    padding: '8px 24px 20px',
  },
  gotItButton: {
    backgroundColor: ACCENT,
    color: '#fff',
    textTransform: 'none',
    width: '100%',
    padding: '10px',
  },
  compactRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '5px',
    padding: '1px 6px',
  },
  compactText: {
    fontSize: '10px',
    color: '#0d47a1',
    opacity: 0.65,
    margin: 0,        // new
  },
};